import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  IconButton,
  InputAdornment,
  Collapse,
  Button,
  Fab,
  CircularProgress,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { getProjects } from "../services/projectService";
import { Project } from "../types";
import ProjectCard from "../components/Cards/ProjectCard";
import { getProjectStatusColor } from "../utils/statusColors";
import { alpha } from "@mui/material";
import Chip from "@mui/material/Chip";
import Notification from "../components/Common/Notification";
import useAcl from "../hooks/useAcl";
import { getFromCache } from "../storage/cacheManager";
import {
  shouldLoadData,
  setLoading as setLoaderLoading,
  clearLoading as clearLoaderLoading,
  getLoadingPromise,
} from "../utils/dataLoader";
import { useNetworkStatus } from "../hooks/useNetworkStatus";

const PROJECTS_FILTERS_STORAGE_KEY = "projects_filters";

type StatusKey = "all" | string;

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { canCreate } = useAcl("Project");
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const createFabRef = useRef<HTMLButtonElement>(null);
  const { isOnline } = useNetworkStatus();

  const initialFilters = sessionStorage.getItem(PROJECTS_FILTERS_STORAGE_KEY)
    ? JSON.parse(sessionStorage.getItem(PROJECTS_FILTERS_STORAGE_KEY)!)
    : {
        search: "",
        statusFilter: ["all"],
      };

  const [search, setSearch] = useState<string>(initialFilters.search || "");
  const [statusFilter, setStatusFilter] = useState<StatusKey[]>(
    initialFilters.statusFilter || ["all"],
  );
  const [page, setPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const rowsPerPage = 10;

  useEffect(() => {
    sessionStorage.setItem(
      PROJECTS_FILTERS_STORAGE_KEY,
      JSON.stringify({ search, statusFilter }),
    );
  }, [search, statusFilter]);

  const loadProjects = useCallback(async (force = false) => {
    setError(null);

    try {
      const cached = await getFromCache<any>("projects:list");
      const cachedList = Array.isArray(cached) ? cached : cached?.list || [];
      if (Array.isArray(cachedList)) {
        setProjects(cachedList);
      }

      if (!navigator.onLine) {
        setLoading(false);
        return;
      }

      const needProjects = force
        ? true
        : await shouldLoadData("projects", null);
      if (!needProjects && cachedList.length > 0) {
        setLoading(false);
        return;
      }

      const existingPromise = getLoadingPromise("projects");
      const projectsPromise =
        existingPromise ||
        getProjects({
          maxSize: 50,
          offset: 0,
          orderBy: "createdAt",
          order: "desc",
        });
      if (!existingPromise) {
        setLoaderLoading("projects", projectsPromise);
      }

      const response = await projectsPromise;
      clearLoaderLoading("projects");
      const projectList = Array.isArray(response)
        ? response
        : response?.list || [];
      setProjects(projectList);
    } catch (err: any) {
      clearLoaderLoading("projects");
      console.error("Error loading projects:", err);
      if (err.response?.status === 403) {
        console.warn("403 Forbidden — используем кэш.");
      } else {
        setError(t("projects.loadError") || "Не удалось загрузить проекты");
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (location.pathname === "/projects" && navigator.onLine) {
      loadProjects(true);
    }
  }, [location.pathname, loadProjects]);

  useEffect(() => {
    const handleFocus = () => {
      if (navigator.onLine) {
        loadProjects(true);
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [loadProjects]);

  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState<string | null>(
    null,
  );

  const handleStatusChange = () => {
    loadProjects();
  };

  const handleStatusUpdateSuccess = (message: string) => {
    setStatusUpdateSuccess(message);
    setTimeout(() => setStatusUpdateSuccess(null), 3000);
  };

  const shadow = "0px 2px 3.6px rgba(0, 0, 0, 0.36)";
  const [projectStatuses, setProjectStatuses] = useState<string[]>([]);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const { getCachedStatuses } = await import("../storage/preloadData");
        const statuses = await getCachedStatuses();
        setProjectStatuses(statuses.projects || []);
      } catch (err) {
        console.error("Failed to load project statuses:", err);
        setProjectStatuses([]);
      }
    };
    loadStatuses();
  }, []);

  const statusOptions: { value: StatusKey; label: string; color?: string }[] = [
    { value: "all", label: t("typeOptions.all") },
    ...projectStatuses.map((status) => {
      const normalizedStatus = status.toLowerCase().replace(/\s/g, "_");
      return {
        value: status as StatusKey,
        label: t(`statuses.project.${normalizedStatus}`, status),
        color: getProjectStatusColor(status),
      };
    }),
  ];

  const filtered = projects.filter((project) => {
    const q = search.toLowerCase();
    const matchesSearch = project.name.toLowerCase().includes(q);
    const matchesStatus =
      statusFilter.includes("all") ||
      statusFilter.includes(project.status as StatusKey);
    return matchesSearch && matchesStatus;
  });

  const pageCount = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const getStatusConfig = (status: string) => {
    const normalized = status.toLowerCase().replace(/\s/g, "_");
    const text = t(`statuses.project.${normalized}`) || status;
    return {
      color: getProjectStatusColor(status),
      translation: text,
    };
  };

  const handleClearSearch = () => {
    setSearch("");
    setPage(1);
  };

  if (loading) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 0, md: 2 }, position: "relative" }}>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
        >
          {t("projects.header")}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          sx={{
            p: 0.5,
            bgcolor: "#E0E6F0",
            color: "#4467A5",
            border: "1px solid #4467A5",
            borderRadius: "8px",
            boxShadow: shadow,
            "&:hover": { bgcolor: "#D5DCE8" },
          }}
          aria-label={
            isFilterOpen ? t("tasks.closeSearch") : t("tasks.filterButton")
          }
        >
          <FilterAltIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: { xs: "none", sm: "block" }, mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ fontSize: "2rem" }}>
          {t("projects.header")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            placeholder={t("projects.searchPlaceholder")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ flex: 1, maxWidth: 400 }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ width: 300 }}>
            <InputLabel>{t("projects.statusLabel")}</InputLabel>
            <Select
              label={t("projects.statusLabel")}
              multiple
              value={statusFilter}
              onChange={(e) => {
                const value = e.target.value as StatusKey[];

                if (value.includes("all") && value.length === 1) {
                  setStatusFilter(["all"]);
                } else if (value.includes("all") && value.length > 1) {
                  setStatusFilter(value.filter((v) => v !== "all"));
                } else if (value.length === 0) {
                  setStatusFilter(["all"]);
                } else {
                  setStatusFilter(value);
                }
                setPage(1);
              }}
              renderValue={(selected) =>
                selected
                  .map(
                    (value) =>
                      statusOptions.find((opt) => opt.value === value)?.label,
                  )
                  .filter(Boolean)
                  .join(", ")
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    },
                  },
                },
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: option.color || "#ccc",
                      marginRight: 1,
                    }}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {canCreate && (
            <Button
              variant="contained"
              onClick={async () => {
                const { available, reason } =
                  await import("../utils/offlineGuard").then((m) =>
                    m.isOfflineActionAvailable("create", t),
                  );
                if (!available) {
                  setError(
                    reason ||
                      t("offline.createUnavailable") ||
                      "Создание недоступно в оффлайн режиме",
                  );
                  return;
                }
                navigate("/project/new");
              }}
              disabled={!isOnline}
              sx={{
                textTransform: "none",
                boxShadow: "0px 2px 3.6px rgba(0, 0, 0, 0.36)",
              }}
            >
              {t("projects.createButton") || "Создать проект"}
            </Button>
          )}
        </Box>
      </Box>
      <Collapse in={isFilterOpen} timeout="auto">
        <Box
          mb={2}
          sx={{
            display: { xs: "flex", sm: "none" },
            flexDirection: "column",
            gap: 1,
          }}
        >
          <TextField
            placeholder={t("projects.searchPlaceholder")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>{t("projects.statusLabel")}</InputLabel>
            <Select
              label={t("projects.statusLabel")}
              multiple
              value={statusFilter}
              onChange={(e) => {
                const value = e.target.value as StatusKey[];
                if (value.includes("all") && value.length === 1) {
                  setStatusFilter(["all"]);
                } else if (value.includes("all") && value.length > 1) {
                  setStatusFilter(value.filter((v) => v !== "all"));
                } else if (value.length === 0) {
                  setStatusFilter(["all"]);
                } else {
                  setStatusFilter(value);
                }
                setPage(1);
              }}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    alignItems: "center",
                    minHeight: 24,
                  }}
                >
                  {selected.map((value) => {
                    const option = statusOptions.find(
                      (opt) => opt.value === value,
                    );
                    const color = option?.color || "#bbb";
                    const label = option?.label || value;

                    return (
                      <Chip
                        key={value}
                        label={label}
                        size="small"
                        sx={{
                          bgcolor: alpha(color, 0.18),
                          color: "rgba(0, 0, 0, 0.87)",
                          border: "none",
                          fontWeight: 500,
                          mr: 0.5,
                          mb: 0.5,
                          ".MuiChip-label": { px: 1 },
                        }}
                        onDelete={
                          value !== "all"
                            ? (e) => {
                                if (
                                  e &&
                                  typeof e.stopPropagation === "function"
                                )
                                  e.stopPropagation();
                                setStatusFilter((prev) => {
                                  const next = prev.filter((v) => v !== value);
                                  return next.length ? next : ["all"];
                                });
                                setPage(1);
                              }
                            : undefined
                        }
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root.Mui-selected": {
                      backgroundColor: "rgba(255, 165, 0, 0.2)",
                    },
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: "rgba(255, 165, 0, 0.2)",
                    },
                  },
                },
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: option.color || "#ccc",
                      marginRight: 1,
                    }}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Collapse>
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Box
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            p: { xs: 1, sm: 2 },
            bgcolor: "background.paper",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {[
              t("projects.columnNames.name"),
              t("projects.columnNames.progress"),
              t("projects.columnNames.endDate"),
              t("projects.columnNames.status"),
            ].map((label, idx) => (
              <Box
                key={idx}
                sx={{
                  flex:
                    idx === 0
                      ? "0 0 25%"
                      : idx === 1
                        ? "0 0 15%"
                        : idx === 2
                          ? "0 0 25%"
                          : "0 0 20%",
                  textAlign: idx === 0 ? "left" : "center",
                }}
              >
                <Typography variant="body1">{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {paginated.length ? (
          paginated.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              getStatusConfig={getStatusConfig}
              onStatusChange={handleStatusChange}
              onStatusUpdateSuccess={handleStatusUpdateSuccess}
            />
          ))
        ) : (
          <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
            {t("projects.noData")}
          </Typography>
        )}
      </Box>
      {pageCount > 1 && (
        <Box mt={2} display="flex" justifyContent="center">
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
          />
        </Box>
      )}
      {canCreate && (
        <Fab
          ref={createFabRef}
          color="primary"
          size="small"
          onClick={async () => {
            const { available, reason } =
              await import("../utils/offlineGuard").then((m) =>
                m.isOfflineActionAvailable("create", t),
              );
            if (!available) {
              setError(
                reason ||
                  t("offline.createUnavailable") ||
                  "Создание недоступно в оффлайн режиме",
              );
              return;
            }
            navigate("/project/new");
          }}
          disabled={!isOnline}
          sx={{
            display: { xs: "flex", sm: "none" },
            position: "fixed",
            bottom: 62,
            right: 16,
            border: "1px solid #4467A5",
            borderRadius: "8px",
            bgcolor: "#4467A5",
            color: "#fff",
            "&:hover": { bgcolor: "#385894" },
            boxShadow: "0px 2px 3.6px rgba(0, 0, 0, 0.36)",
          }}
          aria-label={t("projects.createButton") || "Создать проект"}
        >
          <AddIcon />
        </Fab>
      )}
      {error && (
        <Notification
          open={!!error}
          message={error}
          severity="warning"
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
      )}
      {statusUpdateSuccess && (
        <Notification
          open={!!statusUpdateSuccess}
          message={statusUpdateSuccess}
          severity="success"
          onClose={() => setStatusUpdateSuccess(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
      )}
    </Box>
  );
};

export default Projects;
